import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSettings, FiDatabase, FiKey, FiCheck, FiAlertTriangle, FiCopy, FiExternalLink } = FiIcons;

export default function SetupGuide({ onComplete }) {
  const [step, setStep] = useState(1);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  
  const supabaseUrl = 'https://cjtarocrupqnbwlrujpx.supabase.co';
  const supabaseKey = 'sb_publishable_WxfxSvPbAFeDN6BkFmHNVA_cVtqejI7'; // Gekürzt für Anzeige

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === 'url') {
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } else {
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  const handleStepComplete = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      onComplete();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-4xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiDatabase} className="text-white text-3xl" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Supabase Datenbank Setup</h1>
            <p className="text-gray-600">Reparieren oder erstellen Sie die Datenbankstruktur</p>
          </div>

          {/* Step 2: Database Setup (Direkt angezeigt, da wichtigster Schritt) */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-green-900 mb-4 flex items-center">
                <SafeIcon icon={FiDatabase} className="mr-2" />
                WICHTIG: SQL Befehle ausführen
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Um den Fehler "Table not found" zu beheben, kopieren Sie bitte den folgenden Code und führen ihn im <strong>Supabase SQL Editor</strong> aus.
                </p>
                <div className="bg-white rounded-lg p-4 border border-green-300">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-sm text-gray-600">SQL Editor →</span>
                    <a href="https://supabase.com/dashboard/project/cjtarocrupqnbwlrujpx/sql" target="_blank" className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 flex items-center">
                      <SafeIcon icon={FiExternalLink} className="text-xs mr-1" />
                      Editor öffnen
                    </a>
                  </div>
                </div>
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative">
                  <button 
                    onClick={() => copyToClipboard(`-- 1. App Settings Table
CREATE TABLE IF NOT EXISTS app_settings (
  id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
  created_at timestamptz DEFAULT now(),
  category text NOT NULL,
  setting_key text NOT NULL,
  setting_value text
);
ALTER TABLE app_settings ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Public Access Settings" ON app_settings FOR ALL USING (true) WITH CHECK (true);

-- 2. File Metadata Table
CREATE TABLE IF NOT EXISTS file_metadata (
  id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
  created_at timestamptz DEFAULT now(),
  file_type text NOT NULL,
  file_name text,
  file_size bigint,
  mime_type text
);
ALTER TABLE file_metadata ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Public Access Files" ON file_metadata FOR ALL USING (true) WITH CHECK (true);

-- 3. Terminmarkt Providers Table
CREATE TABLE IF NOT EXISTS terminmarkt_providers (
  id text PRIMARY KEY,
  anbieter_id text,
  created_at timestamptz DEFAULT now(),
  evu text,
  marktart text,
  strom_slp text,
  strom_rlm text,
  gas_slp text,
  gas_rlm text,
  verbrauchsgrenzen_strom text,
  verbrauchsgrenzen_gas text,
  bestellungsrelevante_unterlagen text,
  pflichtangaben text,
  provisionsgrenze text,
  auszahlung text,
  vertragsunterlagen_nach_abschluss text,
  ausgeschlossene_branchen text,
  bemerkung text,
  bonitaetsgrenze text,
  mehr_mindermengen_ja_nein text,
  mehr_mindermengen_mit_prozent text,
  kuendigungsfrist text,
  verlaengerung_aktiv_passiv text,
  buendelung text,
  vollmachten_erlaubt text,
  wechsel_von_fest_zu_spot text,
  wechsel_von_spot_zu_fest text,
  provision_spot text,
  provision_festpreis text,
  provision_tranche text,
  provision_bemerkung_festpreis text,
  quelle text,
  "lastUpdated" text,
  bietet_strom boolean,
  bietet_gas boolean,
  name text,
  "energyType" text,
  "marketType" text
);
ALTER TABLE terminmarkt_providers ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Public Access Termin" ON terminmarkt_providers FOR ALL USING (true) WITH CHECK (true);

-- 4. Spotmarkt Providers Table
CREATE TABLE IF NOT EXISTS spotmarkt_providers (
  id text PRIMARY KEY,
  anbieter_id text,
  created_at timestamptz DEFAULT now(),
  evu text,
  marktart text,
  strom_slp text,
  strom_rlm text,
  gas_slp text,
  gas_rlm text,
  verbrauchsgrenzen_strom text,
  verbrauchsgrenzen_gas text,
  bestellungsrelevante_unterlagen text,
  pflichtangaben text,
  provisionsgrenze text,
  auszahlung text,
  vertragsunterlagen_nach_abschluss text,
  ausgeschlossene_branchen text,
  bemerkung text,
  bonitaetsgrenze text,
  mehr_mindermengen_ja_nein text,
  mehr_mindermengen_mit_prozent text,
  kuendigungsfrist text,
  verlaengerung_aktiv_passiv text,
  buendelung text,
  vollmachten_erlaubt text,
  wechsel_von_fest_zu_spot text,
  wechsel_von_spot_zu_fest text,
  provision_spot text,
  provision_festpreis text,
  provision_tranche text,
  provision_bemerkung_festpreis text,
  quelle text,
  "lastUpdated" text,
  bietet_strom boolean,
  bietet_gas boolean,
  name text,
  "energyType" text,
  "marketType" text
);
ALTER TABLE spotmarkt_providers ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Public Access Spot" ON spotmarkt_providers FOR ALL USING (true) WITH CHECK (true);

-- 5. Provisions Data Table
CREATE TABLE IF NOT EXISTS provisions_data (
  id text PRIMARY KEY,
  anbieter_id text,
  created_at timestamptz DEFAULT now(),
  evu text,
  marktart text,
  strom_slp text,
  strom_rlm text,
  gas_slp text,
  gas_rlm text,
  verbrauchsgrenzen_strom text,
  verbrauchsgrenzen_gas text,
  bestellungsrelevante_unterlagen text,
  pflichtangaben text,
  provisionsgrenze text,
  auszahlung text,
  vertragsunterlagen_nach_abschluss text,
  ausgeschlossene_branchen text,
  bemerkung text,
  bonitaetsgrenze text,
  mehr_mindermengen_ja_nein text,
  mehr_mindermengen_mit_prozent text,
  kuendigungsfrist text,
  verlaengerung_aktiv_passiv text,
  buendelung text,
  vollmachten_erlaubt text,
  wechsel_von_fest_zu_spot text,
  wechsel_von_spot_zu_fest text,
  provision_spot text,
  provision_festpreis text,
  provision_tranche text,
  provision_bemerkung_festpreis text,
  quelle text,
  "lastUpdated" text,
  bietet_strom boolean,
  bietet_gas boolean,
  name text,
  "energyType" text,
  "marketType" text
);
ALTER TABLE provisions_data ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Public Access Prov" ON provisions_data FOR ALL USING (true) WITH CHECK (true);`, 'code')}
                    className="absolute top-2 right-2 bg-gray-700 text-white p-2 rounded hover:bg-gray-600"
                    title="Code kopieren"
                  >
                    <SafeIcon icon={copiedUrl ? FiCheck : FiCopy} />
                  </button>
                  <pre className="text-green-400 text-sm font-mono whitespace-pre-wrap max-h-96 overflow-y-auto">
{`-- 1. App Settings Table
CREATE TABLE IF NOT EXISTS app_settings (
  id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
  created_at timestamptz DEFAULT now(),
  category text NOT NULL,
  setting_key text NOT NULL,
  setting_value text
);
ALTER TABLE app_settings ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Public Access Settings" ON app_settings FOR ALL USING (true) WITH CHECK (true);

-- 2. File Metadata Table
CREATE TABLE IF NOT EXISTS file_metadata (
  id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
  created_at timestamptz DEFAULT now(),
  file_type text NOT NULL,
  file_name text,
  file_size bigint,
  mime_type text
);
ALTER TABLE file_metadata ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Public Access Files" ON file_metadata FOR ALL USING (true) WITH CHECK (true);

-- 3. Terminmarkt Providers Table
CREATE TABLE IF NOT EXISTS terminmarkt_providers (
  id text PRIMARY KEY,
  anbieter_id text,
  created_at timestamptz DEFAULT now(),
  evu text,
  marktart text,
  strom_slp text,
  strom_rlm text,
  gas_slp text,
  gas_rlm text,
  verbrauchsgrenzen_strom text,
  verbrauchsgrenzen_gas text,
  bestellungsrelevante_unterlagen text,
  pflichtangaben text,
  provisionsgrenze text,
  auszahlung text,
  vertragsunterlagen_nach_abschluss text,
  ausgeschlossene_branchen text,
  bemerkung text,
  bonitaetsgrenze text,
  mehr_mindermengen_ja_nein text,
  mehr_mindermengen_mit_prozent text,
  kuendigungsfrist text,
  verlaengerung_aktiv_passiv text,
  buendelung text,
  vollmachten_erlaubt text,
  wechsel_von_fest_zu_spot text,
  wechsel_von_spot_zu_fest text,
  provision_spot text,
  provision_festpreis text,
  provision_tranche text,
  provision_bemerkung_festpreis text,
  quelle text,
  "lastUpdated" text,
  bietet_strom boolean,
  bietet_gas boolean,
  name text,
  "energyType" text,
  "marketType" text
);
ALTER TABLE terminmarkt_providers ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Public Access Termin" ON terminmarkt_providers FOR ALL USING (true) WITH CHECK (true);

-- 4. Spotmarkt Providers Table
CREATE TABLE IF NOT EXISTS spotmarkt_providers (
  id text PRIMARY KEY,
  anbieter_id text,
  created_at timestamptz DEFAULT now(),
  evu text,
  marktart text,
  strom_slp text,
  strom_rlm text,
  gas_slp text,
  gas_rlm text,
  verbrauchsgrenzen_strom text,
  verbrauchsgrenzen_gas text,
  bestellungsrelevante_unterlagen text,
  pflichtangaben text,
  provisionsgrenze text,
  auszahlung text,
  vertragsunterlagen_nach_abschluss text,
  ausgeschlossene_branchen text,
  bemerkung text,
  bonitaetsgrenze text,
  mehr_mindermengen_ja_nein text,
  mehr_mindermengen_mit_prozent text,
  kuendigungsfrist text,
  verlaengerung_aktiv_passiv text,
  buendelung text,
  vollmachten_erlaubt text,
  wechsel_von_fest_zu_spot text,
  wechsel_von_spot_zu_fest text,
  provision_spot text,
  provision_festpreis text,
  provision_tranche text,
  provision_bemerkung_festpreis text,
  quelle text,
  "lastUpdated" text,
  bietet_strom boolean,
  bietet_gas boolean,
  name text,
  "energyType" text,
  "marketType" text
);
ALTER TABLE spotmarkt_providers ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Public Access Spot" ON spotmarkt_providers FOR ALL USING (true) WITH CHECK (true);

-- 5. Provisions Data Table
CREATE TABLE IF NOT EXISTS provisions_data (
  id text PRIMARY KEY,
  anbieter_id text,
  created_at timestamptz DEFAULT now(),
  evu text,
  marktart text,
  strom_slp text,
  strom_rlm text,
  gas_slp text,
  gas_rlm text,
  verbrauchsgrenzen_strom text,
  verbrauchsgrenzen_gas text,
  bestellungsrelevante_unterlagen text,
  pflichtangaben text,
  provisionsgrenze text,
  auszahlung text,
  vertragsunterlagen_nach_abschluss text,
  ausgeschlossene_branchen text,
  bemerkung text,
  bonitaetsgrenze text,
  mehr_mindermengen_ja_nein text,
  mehr_mindermengen_mit_prozent text,
  kuendigungsfrist text,
  verlaengerung_aktiv_passiv text,
  buendelung text,
  vollmachten_erlaubt text,
  wechsel_von_fest_zu_spot text,
  wechsel_von_spot_zu_fest text,
  provision_spot text,
  provision_festpreis text,
  provision_tranche text,
  provision_bemerkung_festpreis text,
  quelle text,
  "lastUpdated" text,
  bietet_strom boolean,
  bietet_gas boolean,
  name text,
  "energyType" text,
  "marketType" text
);
ALTER TABLE provisions_data ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Public Access Prov" ON provisions_data FOR ALL USING (true) WITH CHECK (true);`}
                  </pre>
                </div>
              </div>
            </div>
            
            <div className="flex justify-center">
              <button
                onClick={onComplete}
                className="bg-blue-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors flex items-center"
              >
                <SafeIcon icon={FiCheck} className="mr-2" />
                Fertig! Fenster schließen
              </button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}